(() => {
  let initialized = false;
  let cartUpdateUnsubscriber = null;
  const stickyObservers = [];

  const boxNames = {
    small: 'Small Box',
    medium: 'Medium Box',
    large: 'Large Box',
  };

  function getEngine() {
    return window.ColdSugarShippingBox;
  }

  function getGuides() {
    return Array.from(
      document.querySelectorAll(
        '[data-cs-shipping-box-guide]'
      )
    );
  }

  function getStickies() {
    return Array.from(
      document.querySelectorAll(
        '[data-cs-shipping-box-sticky]'
      )
    );
  }

  function getDocks() {
    return Array.from(
      document.querySelectorAll(
        '[data-cs-shipping-box-dock]'
      )
    );
  }

  function shippingItemCount(
    cartState
  ) {
    return (
      cartState?.items || []
    ).reduce(
      (total, item) => {
        const properties =
          item?.properties || {};

        const mode =
          String(
            properties
              ._cold_sugar_order_mode ||
            ''
          )
            .trim()
            .toLowerCase();

        const isLock =
          Boolean(
            properties
              ._cold_sugar_pickup_lock ||
            properties
              ._cold_sugar_shipping_box_lock
          );

        if (
          mode !== 'shipping' ||
          isLock
        ) {
          return total;
        }

        return (
          total +
          Number(item.quantity || 0)
        );
      },
      0
    );
  }

  function formatRate(rateCents) {
    const cents =
      Number(rateCents || 0);

    return `$${(cents / 100).toFixed(2)}`;
  }

  function roomData(result) {
    const engine = getEngine();

    if (
      !engine ||
      !result?.valid ||
      result.tier === 'empty'
    ) {
      return {
        cookie: 0,
        loafOrMerch: 0,
        overallCookie: 0,
        overallLoafOrMerch: 0,
      };
    }

    const sameCookie =
      engine.maxAdditionalForSameTier(
        result.counts,
        'cookie'
      );

    const sameBread =
      engine.maxAdditionalForSameTier(
        result.counts,
        'bread'
      );

    const sameMerch =
      engine.maxAdditionalForSameTier(
        result.counts,
        'merch'
      );

    const overallCookie =
      engine.maxAdditionalOverall(
        result.counts,
        'cookie'
      );

    const overallBread =
      engine.maxAdditionalOverall(
        result.counts,
        'bread'
      );

    const overallMerch =
      engine.maxAdditionalOverall(
        result.counts,
        'merch'
      );

    return {
      cookie: sameCookie,
      loafOrMerch:
        Math.max(
          sameBread,
          sameMerch
        ),
      overallCookie,
      overallLoafOrMerch:
        Math.max(
          overallBread,
          overallMerch
        ),
    };
  }

  function capacityChoices(result) {
    const room = roomData(result);
    const choices = [];

    if (room.loafOrMerch > 0) {
      choices.push(
        room.loafOrMerch === 1
          ? '1 more loaf or merch'
          : `up to ${room.loafOrMerch} more loaves or merch items`
      );
    }

    if (room.cookie > 0) {
      choices.push(
        room.cookie === 1
          ? '1 more cookie'
          : `up to ${room.cookie} more cookies`
      );
    }

    return choices;
  }

  function transitionCopy(result) {
    const room = roomData(result);

    if (result.tier === 'large') {
      return 'Your Large Box is full.';
    }

    const nextBox =
      result.tier === 'small'
        ? 'Medium'
        : 'Large';

    const cookieCanContinue =
      room.overallCookie > 0;

    const loafCanContinue =
      room.overallLoafOrMerch > 0;

    if (
      cookieCanContinue &&
      loafCanContinue
    ) {
      return `Your current box is full at this rate. Add another cookie, loaf, or merch item and we’ll move you into a ${nextBox} Box.`;
    }

    if (loafCanContinue) {
      return `Your current box is full at this rate. Add a loaf or merch item and we’ll move you into a ${nextBox} Box.`;
    }

    if (cookieCanContinue) {
      return `Your current box is full at this rate. Add another cookie and we’ll move you into a ${nextBox} Box.`;
    }

    return result.message || '';
  }

  function compactCapacityCopy(result) {
    const choices =
      capacityChoices(result);

    if (choices.length === 2) {
      return `Room: ${choices[0]} OR ${choices[1]}`;
    }

    if (choices.length === 1) {
      return `Room: ${choices[0]}`;
    }

    if (result.tier === 'large') {
      return 'Your Large Box is full.';
    }

    const room = roomData(result);
    const nextBox =
      result.tier === 'small'
        ? 'Medium'
        : 'Large';

    if (
      room.overallCookie > 0 &&
      room.overallLoafOrMerch > 0
    ) {
      return `Next item moves you to a ${nextBox} Box.`;
    }

    if (room.overallLoafOrMerch > 0) {
      return `Add a loaf or merch item to move to a ${nextBox} Box.`;
    }

    if (room.overallCookie > 0) {
      return `Add another cookie to move to a ${nextBox} Box.`;
    }

    return '';
  }

  function capacityFillPercent(result) {
    if (!result?.valid) {
      return 0;
    }

    const counts =
      result.counts || {};

    const loaf =
      Number(
        counts.loafEquivalent || 0
      );

    const cookies =
      Number(
        counts.cookie || 0
      );

    if (result.tier === 'merch_only') {
      return Math.min(
        100,
        (Number(counts.merch || 0) / 5) * 100
      );
    }

    if (result.tier === 'small') {
      return Math.min(
        100,
        (cookies / 5) * 100
      );
    }

    if (result.tier === 'medium') {
      if (loaf === 0) {
        return Math.min(
          100,
          (cookies / 10) * 100
        );
      }

      if (cookies === 0) {
        return Math.min(
          100,
          (loaf / 2) * 100
        );
      }

      return Math.min(
        100,
        (
          (loaf / 2) +
          (cookies / 10)
        ) * 100
      );
    }

    if (result.tier === 'large') {
      if (cookies === 0) {
        return Math.min(
          100,
          (loaf / 5) * 100
        );
      }

      /*
        Large mixed boxes have two independent packing dimensions:
        loaf-sized items and cookies. The visual meter blends those
        two dimensions, while the exact choices below it always come
        from the shipping engine.
      */
      return Math.min(
        100,
        (
          (loaf / 4) * 0.5 +
          (cookies / 10) * 0.5
        ) * 100
      );
    }

    return 0;
  }

  function renderGuide(
    guide,
    result
  ) {
    const engine = getEngine();

    if (
      !engine ||
      !guide ||
      !result
    ) {
      return;
    }

    const empty =
      guide.querySelector(
        '[data-cs-shipping-box-empty]'
      );

    const active =
      guide.querySelector(
        '[data-cs-shipping-box-active]'
      );

    const invalid =
      guide.querySelector(
        '[data-cs-shipping-box-invalid]'
      );

    const invalidCopy =
      guide.querySelector(
        '[data-cs-shipping-box-invalid-copy]'
      );

    guide.removeAttribute(
      'data-current-shipping-box'
    );

    if (empty) empty.hidden = true;
    if (active) active.hidden = true;
    if (invalid) invalid.hidden = true;

    if (result.tier === 'empty') {
      guide.dataset.csShippingBoxState =
        'empty';

      if (empty) {
        empty.hidden = false;
      }

      return;
    }

    if (!result.valid) {
      guide.dataset.csShippingBoxState =
        'invalid';

      if (invalid) {
        invalid.hidden = false;
      }

      if (invalidCopy) {
        invalidCopy.textContent =
          result.message ||
          'Please review the shipped items in your cart.';
      }

      return;
    }

    guide.dataset.csShippingBoxState =
      'active';

    guide.dataset.currentShippingBox =
      result.tier;

    if (active) {
      active.hidden = false;
    }

    const title =
      guide.querySelector(
        '[data-cs-shipping-box-active-title]'
      );

    const rate =
      guide.querySelector(
        '[data-cs-shipping-box-active-rate]'
      );

    const label =
      guide.querySelector(
        '[data-cs-shipping-box-active-label]'
      );

    const packed =
      guide.querySelector(
        '[data-cs-shipping-box-packed]'
      );

    const capacityTitle =
      guide.querySelector(
        '[data-cs-shipping-box-capacity-title]'
      );

    const meter =
      guide.querySelector(
        '[data-cs-shipping-box-meter]'
      );

    const meterFill =
      guide.querySelector(
        '[data-cs-shipping-box-meter-fill]'
      );

    const fillOptions =
      guide.querySelector(
        '[data-cs-shipping-box-fill-options]'
      );

    const cardOne =
      guide.querySelector(
        '[data-cs-shipping-box-fill-card="one"]'
      );

    const cardTwo =
      guide.querySelector(
        '[data-cs-shipping-box-fill-card="two"]'
      );

    const copyOne =
      guide.querySelector(
        '[data-cs-shipping-box-fill-copy="one"]'
      );

    const copyTwo =
      guide.querySelector(
        '[data-cs-shipping-box-fill-copy="two"]'
      );

    const fillOr =
      guide.querySelector(
        '[data-cs-shipping-box-fill-or]'
      );

    const capacityNote =
      guide.querySelector(
        '[data-cs-shipping-box-capacity-note]'
      );

    const contents =
      engine.formatContents(
        result.counts
      );

    const choices =
      capacityChoices(result);

    const isMerch =
      result.tier === 'merch_only';

    const displayName =
      isMerch
        ? 'Merch Shipping'
        : boxNames[result.tier] ||
          'Shipping Box';

    if (title) {
      title.textContent =
        displayName;
    }

    if (rate) {
      rate.textContent =
        formatRate(
          result.rateCents
        );
    }

    if (label) {
      label.textContent =
        isMerch
          ? 'M'
          : String(
              result.box || ''
            ).charAt(0);
    }

    if (packed) {
      packed.textContent =
        contents
          ? `${contents} in your box`
          : '';
    }

    if (meterFill) {
      meterFill.style.setProperty(
        '--cs-capacity-fill',
        `${capacityFillPercent(result)}%`
      );
    }

    if (cardOne) {
      cardOne.hidden =
        choices.length < 1;
    }

    if (cardTwo) {
      cardTwo.hidden =
        choices.length < 2;
    }

    if (fillOr) {
      fillOr.hidden =
        choices.length < 2;
    }

    if (copyOne) {
      copyOne.textContent =
        choices[0] || '';
    }

    if (copyTwo) {
      copyTwo.textContent =
        choices[1] || '';
    }

    if (choices.length > 0) {
      if (capacityTitle) {
        capacityTitle.textContent =
          'You still have room for…';
      }

      if (fillOptions) {
        fillOptions.hidden = false;
      }

      if (meter) {
        meter.hidden = false;
      }

      if (capacityNote) {
        capacityNote.hidden = true;
        capacityNote.textContent = '';
      }
    } else {
      if (capacityTitle) {
        capacityTitle.textContent =
          result.tier === 'large'
            ? 'Your box is full'
            : 'Your current box is full';
      }

      if (fillOptions) {
        fillOptions.hidden = true;
      }

      if (meter) {
        meter.hidden = false;
      }

      if (capacityNote) {
        capacityNote.hidden = false;
        capacityNote.textContent =
          isMerch
            ? engine.describeCapacity(
                result
              )
            : transitionCopy(
                result
              );
      }
    }
  }

  function renderSticky(
    sticky,
    result
  ) {
    const engine = getEngine();

    if (
      !sticky ||
      !result ||
      !engine
    ) {
      return;
    }

    if (result.tier === 'empty') {
      sticky.hidden = true;
      return;
    }

    sticky.hidden = false;

    const icon =
      sticky.querySelector(
        '[data-cs-shipping-box-sticky-icon]'
      );

    const title =
      sticky.querySelector(
        '[data-cs-shipping-box-sticky-title]'
      );

    const copy =
      sticky.querySelector(
        '[data-cs-shipping-box-sticky-copy]'
      );

    if (!result.valid) {
      if (icon) icon.textContent = '!';

      if (title) {
        title.textContent =
          'Shipping box needs attention';
      }

      if (copy) {
        copy.textContent =
          result.message ||
          'Review your shipped items.';
      }

      return;
    }

    const isMerch =
      result.tier === 'merch_only';

    if (icon) {
      icon.textContent =
        isMerch
          ? 'M'
          : String(
              result.box || ''
            ).charAt(0);
    }

    if (title) {
      title.textContent =
        isMerch
          ? `Merch Shipping · ${formatRate(result.rateCents)}`
          : `${boxNames[result.tier]} · ${formatRate(result.rateCents)}`;
    }

    if (copy) {
      copy.textContent =
        isMerch
          ? engine.describeCapacity(
              result
            )
          : compactCapacityCopy(
              result
            );
    }
  }

  function renderDock(
    dock,
    result,
    cartState
  ) {
    if (!dock || !result) {
      return;
    }

    if (result.tier === 'empty') {
      dock.hidden = true;
      return;
    }

    dock.hidden = false;

    dock.classList.remove(
      'is-opportunity',
      'is-complete',
      'is-invalid'
    );

    const engine =
      getEngine();

    const icon =
      dock.querySelector(
        '[data-cs-shipping-box-dock-icon]'
      );

    const eyebrow =
      dock.querySelector(
        '[data-cs-shipping-box-dock-eyebrow]'
      );

    const title =
      dock.querySelector(
        '[data-cs-shipping-box-dock-title]'
      );

    const copy =
      dock.querySelector(
        '[data-cs-shipping-box-dock-copy]'
      );

    const meter =
      dock.querySelector(
        '[data-cs-shipping-box-dock-meter]'
      );

    const count =
      dock.querySelector(
        '[data-cs-shipping-box-dock-count]'
      );

    const itemCount =
      shippingItemCount(
        cartState
      );

    if (count) {
      count.textContent =
        itemCount > 0
          ? `· ${itemCount}`
          : '';
    }

    if (meter) {
      meter.style.setProperty(
        '--cs-dock-fill',
        `${capacityFillPercent(result)}%`
      );
    }

    if (!result.valid) {
      dock.classList.add(
        'is-invalid'
      );

      if (icon) {
        icon.textContent = '!';
      }

      if (eyebrow) {
        eyebrow.textContent =
          'Shipping box';
      }

      if (title) {
        title.textContent =
          'Your box needs attention';
      }

      if (copy) {
        copy.textContent =
          result.message ||
          'Review your cart before continuing.';
      }

      return;
    }

    const isMerch =
      result.tier ===
        'merch_only';

    const boxName =
      isMerch
        ? 'Merch shipping'
        : boxNames[result.tier];

    const contents =
      engine &&
      typeof engine.formatContents ===
        'function'
        ? engine.formatContents(
            result.counts
          )
        : '';

    if (icon) {
      icon.textContent =
        isMerch
          ? 'M'
          : String(
              result.box || ''
            ).charAt(0);
    }

    if (eyebrow) {
      eyebrow.textContent =
        `${boxName} · Shipping ${formatRate(result.rateCents)}`;
    }

    if (title) {
      title.textContent =
        contents
          ? `${contents} in your box`
          : boxName;
    }

    const choices =
      capacityChoices(
        result
      );

    if (choices.length > 0) {
      dock.classList.add(
        'is-opportunity'
      );

      if (copy) {
        copy.textContent =
          choices.length === 2
            ? `You still have room: ${choices[0]} OR ${choices[1]}`
            : `You still have room: ${choices[0]}`;
      }

      return;
    }

    dock.classList.add(
      'is-complete'
    );

    if (icon) {
      icon.textContent = '✓';
    }

    if (meter) {
      meter.style.setProperty(
        '--cs-dock-fill',
        '100%'
      );
    }

    if (result.tier === 'large') {
      if (copy) {
        copy.textContent =
          '✓ Your Large Box is full · You’re using all available shipping space.';
      }

      return;
    }

    if (isMerch) {
      if (copy) {
        copy.textContent =
          '✓ Your merch shipment is full at this shipping rate.';
      }

      return;
    }

    const nextBox =
      result.tier === 'small'
        ? 'Medium'
        : 'Large';

    if (copy) {
      copy.textContent =
        `✓ Your ${boxNames[result.tier]} is full · Add another item and we’ll automatically move you into a ${nextBox} Box.`;
    }
  }

  function createNotificationStatus(
    cartState
  ) {
    const engine = getEngine();

    if (
      !engine ||
      typeof engine.summarizeItems !==
        'function'
    ) {
      return null;
    }

    const result =
      engine.summarizeItems(
        cartState?.items || []
      );

    if (result.tier === 'empty') {
      return null;
    }

    const status =
      document.createElement('div');

    status.className =
      'cs-shipping-box-notification-status';

    const kicker =
      document.createElement('span');

    kicker.className =
      'cs-shipping-box-notification-status__kicker';

    const title =
      document.createElement('strong');

    const copy =
      document.createElement('span');

    if (!result.valid) {
      status.classList.add(
        'is-invalid'
      );

      kicker.textContent =
        'Shipping box';

      title.textContent =
        'Your box needs attention';

      copy.textContent =
        result.message ||
        'Review your shipped items before continuing.';
    } else if (
      result.tier === 'merch_only'
    ) {
      const room =
        engine.maxAdditionalForSameTier(
          result.counts,
          'merch'
        );

      kicker.textContent =
        `Merch shipping · ${formatRate(result.rateCents)}`;

      if (room > 0) {
        status.classList.add(
          'is-opportunity'
        );

        title.textContent =
          'You still have room at this shipping rate';

        copy.textContent =
          room === 1
            ? 'Fill it with 1 more merch item.'
            : `Fill it with up to ${room} more merch items.`;
      } else {
        status.classList.add(
          'is-complete'
        );

        title.textContent =
          'Your merch shipment is full';

        copy.textContent =
          'You’re using all available space at this shipping rate.';
      }
    } else {
      const choices =
        capacityChoices(result);

      kicker.textContent =
        `${boxNames[result.tier]} · Shipping ${formatRate(result.rateCents)}`;

      if (choices.length > 0) {
        status.classList.add(
          'is-opportunity'
        );

        title.textContent =
          'You still have room at this shipping rate';

        copy.textContent =
          choices.length === 2
            ? `Fill it with ${choices[0]} OR ${choices[1]}.`
            : `Fill it with ${choices[0]}.`;
      } else if (
        result.tier === 'large'
      ) {
        status.classList.add(
          'is-complete'
        );

        title.textContent =
          'Your Large Box is full';

        copy.textContent =
          'You’re using all available space at this shipping rate.';
      } else {
        const nextBox =
          result.tier === 'small'
            ? 'Medium'
            : 'Large';

        status.classList.add(
          'is-complete'
        );

        title.textContent =
          `Your ${boxNames[result.tier]} is full`;

        copy.textContent =
          `You’re getting the most from this shipping rate. Add another item and we’ll move you into a ${nextBox} Box.`;
      }
    }

    status.append(
      kicker,
      title,
      copy
    );

    return status;
  }

  function renderAll(cartState) {
    const engine = getEngine();
    const guides = getGuides();
    const stickies = getStickies();
    const docks = getDocks();

    if (
      !engine ||
      typeof engine.summarizeItems !==
        'function'
    ) {
      return null;
    }

    if (
      guides.length === 0 &&
      stickies.length === 0 &&
      docks.length === 0
    ) {
      return null;
    }

    const result =
      engine.summarizeItems(
        cartState?.items || []
      );

    guides.forEach(
      (guide) => {
        /*
          The guide inside the information drawer is a static
          reference chart. It should not transform into the live
          cart state while the customer shops.
        */
        if (
          guide.dataset
            .csShippingBoxContext ===
          'info'
        ) {
          return;
        }

        renderGuide(
          guide,
          result
        );
      }
    );

    stickies.forEach(
      (sticky) => {
        renderSticky(
          sticky,
          result
        );
      }
    );

    docks.forEach(
      (dock) => {
        renderDock(
          dock,
          result,
          cartState
        );
      }
    );

    return result;
  }

  function cartRoute() {
    const root =
      window.Shopify?.routes?.root ||
      '/';

    return (
      `${root.replace(/\/?$/, '/')}` +
      'cart.js'
    );
  }

  async function fetchCart() {
    const response =
      await fetch(
        cartRoute(),
        {
          headers: {
            Accept:
              'application/json',
            'X-Requested-With':
              'XMLHttpRequest',
          },
        }
      );

    if (!response.ok) {
      throw new Error(
        'We could not read your cart.'
      );
    }

    return response.json();
  }

  async function refresh(
    cartState = null
  ) {
    const guides = getGuides();
    const stickies = getStickies();
    const docks = getDocks();

    if (
      guides.length === 0 &&
      stickies.length === 0 &&
      docks.length === 0
    ) {
      return null;
    }

    const cart =
      cartState?.items
        ? cartState
        : await fetchCart();

    return renderAll(cart);
  }

  function safeRefresh(
    cartState = null
  ) {
    refresh(cartState)
      .catch((error) => {
        console.error(
          'Cold Sugar shipping box UI refresh failed:',
          error
        );
      });
  }

  function setupStickyVisibility() {
    if (
      typeof IntersectionObserver !==
      'function'
    ) {
      return;
    }

    getStickies().forEach(
      (sticky) => {
        const panel =
          sticky.closest(
            '[data-shop-panel="shipping"]'
          );

        const guide =
          panel?.querySelector(
            '[data-cs-shipping-box-guide][data-cs-shipping-box-context="shop"]'
          );

        if (!guide) {
          return;
        }

        const observer =
          new IntersectionObserver(
            (entries) => {
              entries.forEach(
                (entry) => {
                  sticky.classList.toggle(
                    'is-guide-visible',
                    entry.isIntersecting
                  );
                }
              );
            },
            {
              threshold: 0.08,
            }
          );

        observer.observe(guide);
        stickyObservers.push(
          observer
        );
      }
    );
  }

  function init() {
    if (initialized) return;

    initialized = true;

    setupStickyVisibility();

    document.addEventListener(
      'cart:updated',
      (event) => {
        safeRefresh(
          event.detail?.cart ||
          null
        );
      }
    );

    if (
      typeof window.subscribe ===
        'function' &&
      typeof window.PUB_SUB_EVENTS !==
        'undefined' &&
      window.PUB_SUB_EVENTS.cartUpdate
    ) {
      cartUpdateUnsubscriber =
        window.subscribe(
          window.PUB_SUB_EVENTS.cartUpdate,
          () => {
            safeRefresh();
          }
        );
    } else if (
      typeof subscribe ===
        'function' &&
      typeof PUB_SUB_EVENTS !==
        'undefined' &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      cartUpdateUnsubscriber =
        subscribe(
          PUB_SUB_EVENTS.cartUpdate,
          () => {
            safeRefresh();
          }
        );
    }

    safeRefresh();
  }

  window.ColdSugarShippingBoxUI =
    Object.freeze({
      init,
      refresh,
      renderAll,
      renderGuide,
      fetchCart,
      createNotificationStatus,
      getCartUpdateUnsubscriber:
        () => cartUpdateUnsubscriber,
      getStickyObservers:
        () => stickyObservers.slice(),
    });

  if (
    document.readyState ===
      'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }
})();
